import { PageSkeleton } from '@app/components/page/header/skeleton/PageSkeleton';
import { Route, useRouteMatch } from 'react-router-dom';
import { PageLoader } from '../../../../components/page/header/skeleton/PageLoader';
import { Page } from '../../../../components/page/Page';
import { FragmentContentAiView } from './components/content-ai/FragmentContentAiView';
import { FragmentFilesView } from './components/export/FragmentExportFilesView';
import { FragmentPublishView } from './components/export/FragmentExportPublishView';
import { FragmentTabAccessGuard } from './components/FragmentTabAccessGuard';
import { FragmentsViewHeader } from './components/FragmentsViewHeader';
import { FragmentViewContentSkeleton } from './components/FragmentViewContentSkeleton';
import { FragmentViewPreload } from './components/FragmentViewPreload';
import { FragmentMediaDetail } from './components/media-detail/FragmentMediaDetail';
import { FragmentVideoEditionsTab } from './components/fragment-video-editions-tab/FragmentVideoEditionsTab';
import { useFragmentCanPublish } from './hooks/useFragmentCanPublish';
import { usePreviewFragment } from './hooks/usePreviewFragment';

/**
 * Fragment detail layout: header, tab routes (info, files, publish, edits, Content AI).
 * Fragment record is provided by {@link RecordCtxProvider} in {@link FragmentsViewPage}.
 * Tab routes that depend on media/transcription are gated so deep links match disabled-tab UX.
 */
export function FragmentsViewPage() {
  const { query, fragment } = usePreviewFragment();
  const { path } = useRouteMatch();
  const { canPublish } = useFragmentCanPublish(fragment);

  return (
    <PageLoader
      skeleton={
        <PageSkeleton breadcrumbs={2} tabs={4}>
          <FragmentViewContentSkeleton />
        </PageSkeleton>
      }
      query={query}
    >
      <Page htmlTitle={fragment?.title}>
        <FragmentsViewHeader />
        <Page.Content>
          <FragmentViewPreload />
          <Route
            path={`${path}/content-ai`}
            render={() => (
              <FragmentTabAccessGuard requirement="transcription">
                <FragmentContentAiView />
              </FragmentTabAccessGuard>
            )}
          />
          <Route
            path={`${path}/files`}
            render={() => (
              <FragmentTabAccessGuard requirement="media">
                <FragmentFilesView />
              </FragmentTabAccessGuard>
            )}
          />
          {canPublish && (
            <Route
              path={`${path}/publish`}
              render={() => (
                <FragmentTabAccessGuard requirement="media">
                  <FragmentPublishView />
                </FragmentTabAccessGuard>
              )}
            />
          )}
          <Route
            path={`${path}/edits`}
            render={() => (
              <FragmentTabAccessGuard requirement="transcription">
                <FragmentVideoEditionsTab />
              </FragmentTabAccessGuard>
            )}
          />
          <Route exact path={path} render={() => <FragmentMediaDetail />} />
        </Page.Content>
      </Page>
    </PageLoader>
  );
}
